import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Image } from '@archipro-design/aria';
import { ModuleColumnEnum } from 'generated/graphql';
import type {
    ModuleDataColumn,
    PageBuilderModuleMetaProps,
} from '@modules/page-builder/types';
import { PARAGRAPH_PHOTO_WIDTH } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';

import { getImagesSizes } from '~/utils/image-sizes-utils';
import ProductInfo from '@modules/page-builder/component/common/product-info/ProductInfo';

interface ImageParagraphModuleProps extends PageBuilderModuleMetaProps {
    columns: ModuleDataColumn[];
}

/**
 * A rendered Page Builder module for an image and paragraph.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/image-paragraph-module/ImageParagraphModule.tsx}
 */
const ImageParagraphModule = ({ columns, meta }: ImageParagraphModuleProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    return (
        <div className="mx-auto grid w-full grid-cols-1 gap-x-6 gap-y-7 md:max-w-225 md:grid-cols-2">
            {columns.map((column) => {
                if (column.type === ModuleColumnEnum.ImageColumn) {
                    if (!column.imageUrl && isMobile) {
                        return null;
                    }
                    const imageCol = column.imageUrl ? (
                        <div
                            key={column.id}
                            className="flex flex-col justify-center"
                        >
                            <DetailSingleImageShell
                                caption={column.caption}
                                dimension={column.dimension}
                                libraryImage={column?.libraryImage}
                                tags={column.tags}
                                uniqueApprovedTags={column.uniqueApprovedTags}
                                imageURL={column.imageUrl}
                                taggingEnabled={!column.productId}
                                displayReseneTool={!column.productId && false}
                                isProduct={!!column.productId}
                            >
                                <Image
                                    src={column.imageUrl}
                                    layout={'fill'}
                                    objectFit={'cover'}
                                    width={column.dimension.width}
                                    height={column.dimension.height}
                                    sizes={getImagesSizes(
                                        PARAGRAPH_PHOTO_WIDTH
                                    )}
                                    alt={
                                        column.caption ||
                                        column?.libraryImage?.Title ||
                                        ''
                                    }
                                    variables={{
                                        mixBlendMode: column.productId
                                            ? 'multiply'
                                            : 'normal',
                                    }}
                                />
                            </DetailSingleImageShell>
                        </div>
                    ) : (
                        <div key={column.id}></div>
                    );
                    return column.productId ? (
                        <ProductInfo
                            key={column.id}
                            productId={column.productId}
                            dimension={column.dimension}
                            variant={'02'}
                            image={imageCol}
                            clickable={true}
                        />
                    ) : (
                        imageCol
                    );
                } else if (column.type === ModuleColumnEnum.TextColumn) {
                    if (!column.content && isMobile) {
                        return null;
                    }
                    return (
                        <SingleParagraphModule
                            key={column.id}
                            column={column}
                            meta={meta}
                        />
                    );
                }
                return null;
            })}
        </div>
    );
};

export default ImageParagraphModule;
